import CountUp from "react-countup";
import { Props_SnippetStatistic } from "./Statistic.props";
import { CompileHeadingToSpan, CompileMarkdown } from '../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler'
import { useId } from 'react';

export const parseStatConfig = (str: string) => {
    const stringToParse = str.trim();
    const decimalMatch = stringToParse.match(/([.,])(\d+)$/); 
    const integerPart = decimalMatch ? stringToParse.slice(0, -decimalMatch[0].length) : stringToParse;
    const separator = integerPart.match(/[ .,]/)?.[0] || '';
    const parsedValue = parseFloat(integerPart.replace(/\D/g, '') + (decimalMatch ? `.${decimalMatch[2]}` : ''));

    return {
        isNumeric: !isNaN(parsedValue) && /\d/.test(stringToParse),
        end: parsedValue || 0,
        decimals: decimalMatch ? decimalMatch[2].length : 0,
        decimal: decimalMatch ? decimalMatch[1] : ',',
        separator: separator 
    };
};

const Statistic = (props: Props_SnippetStatistic ) => {
    const snippetId = useId();

    const {
        heading,
        layout,
        animation,
        repeatable_statisticItems: items = [],
    } = props;

    const layoutStyle = layout?.style?.value || 'horizontal';
    const backgroundColour = layout?.backgroundColour?.value || 'tertiary';
    const backgroundImage = layout?.backgroundImage?.value || '';
    const textColour = layout?.textColour?.value || 'white';
    const textSize = layout?.textSize?.value || 'extra-large';
    const animationEnabled = animation?.enabled?.value ?? true;

    const backgroundColourClass = !backgroundImage && backgroundColour ? `snippet-statistic--bg--${backgroundColour}` : '';
    const backgroundImageClass = backgroundImage ? 'snippet-statistic--bg-image' : '';
    const textColourClass = textColour ? `snippet-statistic--text-${textColour}` : '';
    const sectionClasses = `snippet-statistic snippet-statistic--layout-${layoutStyle} ${backgroundColourClass} ${backgroundImageClass} ${textColourClass}`;
    const sectionStyle = backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : undefined;
    const headingClasses = `snippet-statistic__heading ${heading?.visibility.value ? '' : 'snippet-statistic__heading--hidden'}`;

    const baseDelay = 0.3;
    const delayIncrement = 0.15;

    const processedItems = items.map((item, index) => {
        const { value, prefix, suffix, description, icon, iconAltText } = item;
        const rawValue = value?.value ?? '';
        const config = parseStatConfig(rawValue);
        const delay = baseDelay + (index * delayIncrement);
        return {
            key: `snippet-statistic-${snippetId}-item-${index}`,
            rawValue,
            config,
            delay,
            prefix,
            suffix,
            description,
            icon,
            iconAltText,
        };
    });

    return (
        <section 
            className={sectionClasses}
            style={sectionStyle}
        >  
            <div className="snippet-statistic__container">
                    <h2 className={headingClasses}>
                    {heading?.text?.value && CompileHeadingToSpan(heading.text.value)}
                    </h2>

                <ul className="snippet-statistic__items">
                    {processedItems.map(({
                        key, rawValue, config, delay, prefix, suffix, description, icon, iconAltText
                    }) => (
                        <li 
                            key={key} 
                            className={`snippet-statistic__item snippet-statistic__item--size-${textSize}`}
                        >
                            {icon?.value && (
                                <div className="snippet-statistic__icon-container">
                                    <img
                                        src={icon.value}
                                        alt={iconAltText?.value}
                                        className="snippet-statistic__icon"
                                        loading="lazy"
                                        width="64"
                                        height="64"
                                    />
                                </div>
                            )}

                            <span
                                className="snippet-statistic__value"
                                aria-hidden="true"
                            >
                                {animationEnabled && config.isNumeric ? (
                                    <CountUp
                                        start={0}
                                        end={config.end}
                                        duration={2}
                                        delay={delay}
                                        decimals={config.decimals}
                                        decimal={config.decimal}
                                        separator={config.separator}
                                        prefix={prefix?.value}
                                        suffix={suffix?.value}
                                        enableScrollSpy
                                        scrollSpyOnce
                                        scrollSpyDelay={delay * 1000}
                                    >
                                        {({ countUpRef }) => <span ref={countUpRef} />}
                                    </CountUp>
                                ) : (
                                    <>
                                        {prefix?.value}{rawValue}{suffix?.value}
                                    </>
                                )}
                            </span>
                            <span className="snippet-statistic__value--sr">
                                {prefix?.value}{rawValue}{suffix?.value}
                            </span>

                            {description?.value && (
                                <span className="snippet-statistic__description">
                                    {CompileMarkdown(description.value)}
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
};

export default Statistic;